/**
 * WebSocket Service for Admin Dashboard (Socket.IO Implementation)
 * 
 * Provides real-time updates using Socket.IO standard protocol.
 */

import { useState, useEffect } from 'react';
import { io, Socket } from 'socket.io-client';
import { QueryClient } from '@tanstack/react-query';
import { getToken } from '@/services/authService';
import { resolveAuthoritativeSocketUrl } from '../config/api.config';

// --- Types ---

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export interface WebSocketMessage {
  type: 'product' | 'order';
  action: 'create' | 'update' | 'delete' | 'updated';
  data: {
    _id: string;
    [key: string]: unknown;
  };
  message?: string;
  timestamp?: string;
  productId?: string;
  product?: {
    _id: string;
    [key: string]: unknown;
  };
  severity?: 'info' | 'warning' | 'error' | 'success';
}

export interface WebSocketDependencies {
  authState: { status: 'authenticated' | 'unauthenticated' | 'loading' };
  queryClient: QueryClient;
  setStatus: (status: ConnectionStatus) => void;
}

export interface WebSocketHookResult {
  status: ConnectionStatus;
  lastMessage: WebSocketMessage | null;
  addProductUpdateListener: (callback: (data: WebSocketMessage['data']) => void) => void;
  addOrderUpdateListener: (callback: (data: WebSocketMessage['data']) => void) => void;
}

type MessageListener = (data: WebSocketMessage['data']) => void;

// --- Singleton Service ---

// Derive URL: Request http://api.gffpk.com -> http://api.gffpk.com (Socket.io adds /socket.io/)
// Sovereign Host-Aware Gateway WebSocket Resolution (STORY-EPIC-75.2)

class SocketService {
  private socket: Socket | null = null;
  private productListeners = new Set<MessageListener>();
  private orderListeners = new Set<MessageListener>();
  private static instance: SocketService;

  private constructor() { }

  public static getInstance(): SocketService {
    if (!SocketService.instance) {
      SocketService.instance = new SocketService();
    }
    return SocketService.instance;
  }

  public connect(setStatus: (s: ConnectionStatus) => void, onMessage: (msg: WebSocketMessage) => void) {
    if (this.socket?.connected) {
      setStatus('connected');
      this.ensureRooms();
      return;
    }

    const token = getToken();
    if (!token) {
      console.warn('[Socket] No token for handshake');
      return;
    }

    // Clean token
    const cleanToken = token.startsWith('Bearer ') ? token.substring(7) : token;

    setStatus('connecting');

    const socketUrl = resolveAuthoritativeSocketUrl();

    this.socket = io(socketUrl, {
      auth: { token: cleanToken },
      transports: ['polling', 'websocket'],
      upgrade: true,
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      // path defaults to /socket.io/
    });

    this.socket.on('connect', () => {
      // console.debug('[Socket] Connected:', this.socket?.id);
      setStatus('connected');
      this.ensureRooms();
    });

    this.socket.on('disconnect', (reason) => {
      // console.debug('[Socket] Disconnected:', reason);
      setStatus('disconnected');
    });

    this.socket.on('connect_error', (err) => {
      // console.error('[Socket] Connection Error:', err);
      setStatus('error');
    });

    // --- Message Handling ---
    // Backend emits: 'ORDER_CREATED', 'ORDER_UPDATED', etc.
    // The previous implementation utilized a generic message structure. 
    // We will normalize specific events into the generic structure for compatibility.

    this.socket.on('ORDER_CREATED', (data) => {
      // console.debug('[Socket] ⚡ Raw Event: ORDER_CREATED', data);
      const msg: WebSocketMessage = { type: 'order', action: 'create', data: data.order || data };
      this.notify(msg, onMessage);
    });

    this.socket.on('ORDER_UPDATED', (data) => {
      const msg: WebSocketMessage = { type: 'order', action: 'update', data: data };
      this.notify(msg, onMessage);
    });

    // WO-2001: The Audible Kitchen (Stripe/Safepay Payment Emission)
    this.socket.on('order:paid', (data) => {
      const msg: WebSocketMessage = { type: 'order', action: 'update', data: data };
      this.notify(msg, onMessage);
    });

    // Generic 'message' fallback if used
    this.socket.on('message', (data) => {
      this.notify(data, onMessage);
    });

    // ADR-112: Trajectory Updates
    this.socket.on('trajectory:update', (data) => {
      // Logic handled by specific listeners or normalized into a generic format
      // For now, we'll keep it as a dedicated listener in the service
    });
  }

  private notify(msg: WebSocketMessage, onMessage: (msg: WebSocketMessage) => void) {

    try {
      onMessage(msg);
    } catch (err) {
      console.warn('[SocketService] ⚠️ Global hook error (non-fatal):', err);
    }

    if (msg.type === 'product') {
      this.productListeners.forEach(l => { try { l(msg.data || msg.product); } catch (e) { console.error(e); } });
    } else if (msg.type === 'order') {
      this.productListeners.forEach(l => { try { l(msg.data); } catch (e) { console.error(e); } }); // Legacy?
      this.orderListeners.forEach(l => {
        try { l(msg.data); } catch (e) { console.error('[SocketService] Listener failed:', e); }
      });
    }
  }

  public disconnect() {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
  }

  public addProductListener(cb: MessageListener) { this.productListeners.add(cb); }
  public addOrderListener(cb: MessageListener) {
    this.orderListeners.add(cb);
  }

  public removeProductListener(cb: MessageListener) { this.productListeners.delete(cb); }
  public removeOrderListener(cb: MessageListener) {
    this.orderListeners.delete(cb);
  }

  public getStatus(): ConnectionStatus {
    return this.socket?.connected ? 'connected' : 'disconnected';
  }

  private ensureRooms() {
    if (!this.socket) return;
    this.socket.emit('join_kitchen');
    this.socket.emit('join_logistics');
    
    // Auto-join regional logistics if env available
    const region = (import.meta.env.VITE_REGION_CODE || '').toLowerCase();
    if (region) {
      this.socket.emit('join_logistics_regional', region);
    }
  }

  // WO-807 Ticket 4: The Beacon
  public emitDriverLocation(payload: { driverId: string; orderId?: string; location: any }) {
    if (this.socket && this.socket.connected) {
      // Volatile event - we don't care if it's dropped if disconnected
      this.socket.volatile.emit('rider_location_update', payload);
    }
  }

  public onDriverLocation(callback: (payload: any) => void) {
    if (this.socket) {
      this.socket.on('rider_location_update', callback);
    }
  }

  public offDriverLocation(callback: (payload: any) => void) {
    if (this.socket) {
      this.socket.off('rider_location_update', callback);
    }
  }

  // ADR-112: Trajectory Observers
  public onTrajectoryUpdate(callback: (payload: any) => void) {
    if (this.socket) {
      this.socket.on('trajectory:update', callback);
    }
  }

  public offTrajectoryUpdate(callback: (payload: any) => void) {
    if (this.socket) {
      this.socket.off('trajectory:update', callback);
    }
  }

  // WO-2001: The Audible Kitchen (Manual Override)
  public emitForceProduction(order: any) {
    if (this.socket && this.socket.connected) {
      this.socket.emit('admin_force_production', order);
    }
  }

  public on(event: string, callback: (...args: any[]) => void) {
    if (this.socket) {
      this.socket.on(event, callback);
    }
  }

  public off(event: string, callback: (...args: any[]) => void) {
    if (this.socket) {
      this.socket.off(event, callback);
    }
  }
}

// --- Hook Implementation ---

export const addProductUpdateListener = (callback: MessageListener): void => {
  SocketService.getInstance().addProductListener(callback);
};
export const removeProductUpdateListener = (callback: MessageListener): void => {
  SocketService.getInstance().removeProductListener(callback);
};

export const addOrderUpdateListener = (callback: MessageListener): void => {
  SocketService.getInstance().addOrderListener(callback);
};
export const removeOrderUpdateListener = (callback: MessageListener): void => {
  SocketService.getInstance().removeOrderListener(callback);
};

export const emitDriverLocation = (payload: { driverId: string; orderId?: string; location: any }): void => {
  SocketService.getInstance().emitDriverLocation(payload);
};

export const addDriverLocationListener = (callback: (payload: any) => void): void => {
  SocketService.getInstance().onDriverLocation(callback);
};

export const removeDriverLocationListener = (callback: (payload: any) => void): void => {
  SocketService.getInstance().offDriverLocation(callback);
};

export const addTrajectoryListener = (callback: (payload: any) => void): void => {
  SocketService.getInstance().onTrajectoryUpdate(callback);
};

export const removeTrajectoryListener = (callback: (payload: any) => void): void => {
  SocketService.getInstance().offTrajectoryUpdate(callback);
};

export const emitForceProduction = (order: any): void => {
  SocketService.getInstance().emitForceProduction(order);
};

const useWebSocket = ({ authState, queryClient, setStatus }: WebSocketDependencies): WebSocketHookResult => {
  const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);

  useEffect(() => {
    const service = SocketService.getInstance();

    if (authState.status === 'authenticated') {
      service.connect(setStatus, (msg) => {
        setLastMessage(msg);

        // Invalidation Logic (Ported from old hook)
        // Simplified Invalidation with Optional Chaining for Safety
        if (msg.type === 'product') {
          queryClient?.invalidateQueries?.({ predicate: q => q.queryKey[0] === 'products' });
        } else if (msg.type === 'order') {
          queryClient?.invalidateQueries?.({ predicate: q => q.queryKey[0] === 'orders' });
        }
      });
    } else {
      // service.disconnect(); // Optional: keep alive? Usually better to disconnect on logout
    }

    // Check status on mount
    setStatus(service.getStatus());

    return () => {
      // We don't necessarily disconnect on unmount of one component if shared, 
      // but for now let's leave it persistent or managed by Auth presence.
    };
  }, [authState.status, queryClient]); // Removed setStatus from dependency to avoid loop if unstable

  return {
    status: SocketService.getInstance().getStatus(),
    lastMessage,
    addProductUpdateListener: (cb) => SocketService.getInstance().addProductListener(cb),
    addOrderUpdateListener: (cb) => SocketService.getInstance().addOrderListener(cb),
  };
};

export { useWebSocket, SocketService };
const websocketService = Object.assign(SocketService, { useWebSocket, SocketService });
export default websocketService;
